/**
 * Time words for the Strip Card surfaces. Every time a fan reads is in the
 * zone they are viewing from (SCORES-9 / Q67), read once per render through
 * `resolveViewerTimeZone`. Labels are assembled from `formatToParts` so the
 * spacing never depends on the browser's ICU build (a narrow no-break space
 * before AM and PM on one engine, a plain space on another).
 */
import { todayInZone } from "../scores/calendarDay";

type typeFormatterKind = "kickoff" | "date";

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(
  kind: typeFormatterKind,
  timeZone: string,
): Intl.DateTimeFormat {
  const key = `${kind}:${timeZone}`;
  const cached = formatterCache.get(key);
  if (cached !== undefined) return cached;
  const formatter =
    kind === "kickoff"
      ? new Intl.DateTimeFormat("en-US", {
          timeZone,
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        })
      : new Intl.DateTimeFormat("en-US", {
          timeZone,
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  formatterCache.set(key, formatter);
  return formatter;
}

function partsOf(
  kind: typeFormatterKind,
  iso: string,
  timeZone: string,
): Record<string, string> | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const parts: Record<string, string> = {};
  for (const part of formatterFor(kind, timeZone).formatToParts(new Date(ms))) {
    parts[part.type] = part.value;
  }
  return parts;
}

/** "Sun, Nov 8 at 1:00 PM EST", or "" when the time does not parse. */
export function stripKickoffLabel(input: {
  kickoffAt: string;
  timeZone: string;
}): string {
  const parts = partsOf("kickoff", input.kickoffAt, input.timeZone);
  if (parts === null) return "";
  const zone = parts.timeZoneName === undefined ? "" : ` ${parts.timeZoneName}`;
  return `${parts.weekday ?? ""}, ${parts.month ?? ""} ${parts.day ?? ""} at ${parts.hour ?? ""}:${parts.minute ?? ""} ${parts.dayPeriod ?? ""}${zone}`;
}

/** "Sun, Nov 8, 1:00 PM", the short form a game switcher option uses. */
export function stripShortKickoffLabel(input: {
  kickoffAt: string;
  timeZone: string;
}): string {
  const parts = partsOf("kickoff", input.kickoffAt, input.timeZone);
  if (parts === null) return "";
  return `${parts.weekday ?? ""}, ${parts.month ?? ""} ${parts.day ?? ""}, ${parts.hour ?? ""}:${parts.minute ?? ""} ${parts.dayPeriod ?? ""}`;
}

/** "Nov 8, 2026", or "" when the time does not parse. */
export function stripDateLabel(input: {
  iso: string;
  timeZone: string;
}): string {
  const parts = partsOf("date", input.iso, input.timeZone);
  if (parts === null) return "";
  return `${parts.month ?? ""} ${parts.day ?? ""}, ${parts.year ?? ""}`;
}

/** The calendar day of `iso` in `timeZone`, "YYYY-MM-DD", or "" when it does
 *  not parse. Seeds a date field from a stored winner time. */
export function stripDayOf(input: { iso: string; timeZone: string }): string {
  const ms = Date.parse(input.iso);
  if (Number.isNaN(ms)) return "";
  return todayInZone({ nowMs: ms, timeZone: input.timeZone });
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The instant a winner is stored at for a picked day: noon on that day in the
 * browser's own zone, which is the zone the fan views from. Noon keeps the day
 * the same when another member reads it a few zones away. Null when `day` is
 * not a "YYYY-MM-DD".
 */
export function stripWonAtFromDay(day: string): string | null {
  const match = DAY_PATTERN.exec(day);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const dayOfMonth = Number(match[3]);
  const date = new Date(year, month - 1, dayOfMonth, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}
